// Frame-specific image optimization for Lumaprints orders
import { uploadBase64Image } from './storage';

interface FrameDimensions {
  width: number;
  height: number;
}

// Frame size requirements for Lumaprints - Updated with exact specifications
// All dimensions at 300 DPI (landscape orientation for XS: 7"W x 5"H)
const FRAME_DIMENSIONS: Record<string, FrameDimensions> = {
  'XS': { width: 2100, height: 1500 },  // 7" x 5" at 300 DPI (landscape)
  'S': { width: 3600, height: 2400 },
  'M': { width: 5100, height: 3300 },
  'L': { width: 7200, height: 5400 }
};

/**
 * Optimize image for specific frame size according to Lumaprints requirements
 */
export async function optimizeImageForFrame(
  imageUrl: string,
  frameSize: 'XS' | 'S' | 'M' | 'L'
): Promise<string> {
  const dimensions = FRAME_DIMENSIONS[frameSize];
  
  if (!dimensions) {
    throw new Error(`Unknown frame size: ${frameSize}`);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = async () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Set canvas to target dimensions
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;

        // Calculate scaling and positioning - all frames follow: Scale width, crop height
        const { sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight } = 
          calculateScaleWidthCropHeight(img.width, img.height, dimensions.width, dimensions.height);

        // Fill with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Enable high-quality image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Draw the optimized image
        ctx.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight,  // Source rectangle
          destX, destY, destWidth, destHeight            // Destination rectangle
        );

        // Convert to high-quality base64
        const optimizedImageData = canvas.toDataURL('image/jpeg', 0.95);
        
        // Upload to Supabase storage
        const filename = `optimized-${frameSize}-${Date.now()}.jpg`;
        const uploadedUrl = await uploadBase64Image(optimizedImageData, 'propertyimages', filename);
        
        resolve(uploadedUrl);
        
      } catch (error) {
        console.error('Error in frame optimization:', error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('Error loading image for optimization:', error);
      reject(new Error('Failed to load image for optimization'));
    };
    
    // Add cache buster to avoid CORS issues
    img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
  });
}

/**
 * Calculate scaling and cropping parameters
 * ALL frames follow the same pattern: Scale to target width, then center crop to target height
 */
function calculateScaleWidthCropHeight(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): {
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
  destX: number;
  destY: number;
  destWidth: number;
  destHeight: number;
} {
  // Step 1: Scale the entire image so the width becomes exactly the target width
  const scaleForWidth = targetWidth / sourceWidth;
  const scaledHeight = sourceHeight * scaleForWidth;
  
  if (scaledHeight >= targetHeight) {
    // Step 2A: Scaled image is taller than target, so center crop the height
    const cropHeight = targetHeight / scaleForWidth;
    const cropOffsetY = (sourceHeight - cropHeight) / 2;
    
    return {
      sourceX: 0,
      sourceY: cropOffsetY,
      sourceWidth: sourceWidth,
      sourceHeight: cropHeight,
      destX: 0,
      destY: 0,
      destWidth: targetWidth,
      destHeight: targetHeight
    };
  } else {
    // Step 2B: Scaled image is shorter than target
    // Still prioritize width scaling and add padding/stretching for height
    return {
      sourceX: 0,
      sourceY: 0,
      sourceWidth: sourceWidth,
      sourceHeight: sourceHeight,
      destX: 0,
      destY: 0,
      destWidth: targetWidth,
      destHeight: targetHeight
    };
  }
}

/**
 * Get frame dimensions for a given size
 */
export function getFrameDimensions(frameSize: 'XS' | 'S' | 'M' | 'L'): FrameDimensions {
  return FRAME_DIMENSIONS[frameSize];
}